import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, TableCell, TableRow } from '@mui/material';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { ROLES_AND_PERMISSION__ACCOUNT_TYPE } from '../../../../../context/auth';
import {
	DEPRECATED__CLAIMS_ONLY_INTERNAL,
	DEPRECATED__CLAIMS_ONLY_LABEL,
	DEPRECATED__FULL_ACCESS_INTERNAL,
	DEPRECATED__FULL_ACCESS_LABEL,
	DEPRECATED__READ_ONLY_INTERNAL,
	DEPRECATED__READ_ONLY_LABEL,
	NOT_AVAILABLE_LABEL,
} from '../../../../../data/labelsAndMessages';
import { dayjs } from '../../../../../date-util';
import {
	DISABLED_USER_TEXT,
	EMPTY_TABLE_LABEL,
	ENABLED_USER_TEXT,
	USERS_EDIT_USER_DIALOG_TITLE,
	USERS_FORM_MIGRATION_PENDING_LABEL,
	USERS_FORM_NO_ROLES_LABEL,
	USERS_TABLE_CREATED_HEADER,
	USERS_TABLE_STATUS_HEADER,
} from '../../data/constants';
import { handleModalClose } from '../../utils/handleModalClose';
import UserForm from './UserForm';
import { UserRowProps } from './Users.types';

const UserRow = (props: UserRowProps) => {
	const { user, rolesAndPermissionEnabled } = props;
	const {
		username,
		accountType,
		name,
		roles,
		email,
		createdTimestamp,
		deleted,
	} = user;
	const [open, setOpen] = React.useState(false);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const parseDate = (timestamp: string) => {
		//format: June 15th, 2022
		const parsedDate = dayjs(parseInt(timestamp));
		const formattedDate = parsedDate.format('LL');
		return formattedDate;
	};

	const parsePermissions = (accountType: string) => {
		if (accountType === DEPRECATED__FULL_ACCESS_INTERNAL())
			return DEPRECATED__FULL_ACCESS_LABEL();
		if (accountType === DEPRECATED__READ_ONLY_INTERNAL())
			return DEPRECATED__READ_ONLY_LABEL();
		if (accountType === DEPRECATED__CLAIMS_ONLY_INTERNAL())
			return DEPRECATED__CLAIMS_ONLY_LABEL();
		return EMPTY_TABLE_LABEL();
	};

	const displayEmail = (email: string) => {
		if (!email) return EMPTY_TABLE_LABEL();

		let display = email;
		const maxLen = 25;

		if (email.length > maxLen) {
			display = email.substring(0, maxLen);
			display += '...';
		}

		return display;
	};

	const StatusChip = (disabled: boolean) => {
		const label = disabled ? DISABLED_USER_TEXT() : ENABLED_USER_TEXT();
		const color = disabled ? 'error' : 'success';
		return <Chip variant='outlined' label={label} size='small' color={color} />;
	};

	return (
		<>
			<TableRow key={user.name}>
				<TableCell
					sx={{
						maxWidth: '400px',
						wordWrap: 'break-word',
						verticalAlign: 'baseline',
					}}
				>
					{username}
				</TableCell>
				<TableCell sx={{ verticalAlign: 'baseline' }}>{name}</TableCell>
				<TableCell sx={{ verticalAlign: 'baseline' }}>
					{displayEmail(email)}
				</TableCell>
				{!rolesAndPermissionEnabled && (
					<TableCell>{parsePermissions(accountType)}</TableCell>
				)}
				{rolesAndPermissionEnabled && (
					<TableCell sx={{ width: 8 * 40 }}>
						{roles.length > 0
							? roles
									.sort()
									.map((role, index) => (
										<Chip
											size='small'
											color='secondary'
											variant='filled'
											key={index}
											label={role.displayName}
											sx={{ m: 0.5 }}
										/>
									))
							: ROLES_AND_PERMISSION__ACCOUNT_TYPE === accountType && (
									<Chip
										variant='outlined'
										color='error'
										label={USERS_FORM_NO_ROLES_LABEL()}
										size='small'
										sx={{ m: 0.5 }}
									/>
							  )}
						{ROLES_AND_PERMISSION__ACCOUNT_TYPE !== accountType && (
							<Chip
								variant='outlined'
								color='warning'
								label={
									parsePermissions(accountType) +
									USERS_FORM_MIGRATION_PENDING_LABEL()
								}
								size='small'
								sx={{ m: 0.5 }}
							/>
						)}
					</TableCell>
				)}
				<TableCell sx={{ verticalAlign: 'baseline' }}>
					{StatusChip(deleted)}
				</TableCell>
				<TableCell sx={{ verticalAlign: 'baseline' }}>
					{user.createdTimestamp === '0'
						? NOT_AVAILABLE_LABEL()
						: parseDate(createdTimestamp)}
				</TableCell>
				<TableCell sx={{ verticalAlign: 'baseline' }}>
					<IconButton aria-label='edit' size='small' onClick={handleOpen}>
						<EditIcon />
					</IconButton>
				</TableCell>
			</TableRow>
			<Dialog open={open} onClose={(e, r) => handleModalClose(r, handleClose)}>
				<DialogTitle>{USERS_EDIT_USER_DIALOG_TITLE()}</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<Stack
						direction={'row'}
						spacing={2}
						justifyContent='space-between'
						sx={{ mb: 3 }}
					>
						<Box>
							<Typography variant='body1'>
								{USERS_TABLE_STATUS_HEADER()}
							</Typography>
							{StatusChip(user.deleted)}
						</Box>
						{user.createdTimestamp !== '0' && (
							<Box>
								<Typography variant='body1'>
									{USERS_TABLE_CREATED_HEADER()}
								</Typography>
								<Typography variant='body1' sx={{ fontWeight: 'medium' }}>
									{parseDate(user.createdTimestamp)}
								</Typography>
							</Box>
						)}
					</Stack>
					<UserForm
						isEdit
						handleClose={handleClose}
						defaultValues={user}
						rolesAndPermissionEnabled={rolesAndPermissionEnabled}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default UserRow;
